import React, { useState } from 'react';
import { Select, Button, Card, Typography, message as antdMessage, Alert, Spin } from 'antd';
import { BuildOutlined } from '@ant-design/icons';
import { selectWerk, WerkSelectionPayload, WerkSelectionApiResponse, MrpData } from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

interface WerkSelectionProps {
  availableWerks: string[];
  mrpDataframeJson: string;
  onWerkSelected: (data: WerkSelectionApiResponse) => void;
  disabled: boolean;
}

const WerkSelection: React.FC<WerkSelectionProps> = ({ 
  availableWerks, 
  mrpDataframeJson, 
  onWerkSelected, 
  disabled 
}) => {
  const [selectedWerk, setSelectedWerk] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleWerkChange = (value: string) => {
    setSelectedWerk(value);
    setError(null);
    console.log(`Werk ausgewählt: ${value}`);
  };

  const handleConfirmSelection = async () => {
    if (!selectedWerk) {
      antdMessage.error('Bitte wählen Sie zuerst ein Werk aus.');
      return;
    }

    setLoading(true);
    setError(null);

    console.log('Bestätige Werk-Auswahl...');
    console.log(`Ausgewähltes Werk: ${selectedWerk}`);
    console.log(`MRP Datenframe Größe: ${(new Blob([mrpDataframeJson]).size / 1024).toFixed(2)} KB`);

    const payload: WerkSelectionPayload = {
      mrp_dataframe_json: mrpDataframeJson,
      selected_werk: selectedWerk,
    };

    try {
      const response = await selectWerk(payload);
      
      onWerkSelected(response.data);
      antdMessage.success(`Werk "${selectedWerk}" erfolgreich ausgewählt! MRP-Daten für dieses Werk verarbeitet.`);
      
      console.log('Werk-Auswahl erfolgreich abgeschlossen');
      console.log(`${Object.keys(response.data.mrp_data.eigen_dict).length} Materialien für Werk verarbeitet: ${selectedWerk}`);
      
    } catch (err: any) {
      const apiError = err.response?.data?.detail || err.message || 'Fehler bei der Verarbeitung der Werk-Auswahl.';
      setError(apiError);
      antdMessage.error(apiError);
      
      console.error('Werk-Auswahl fehlgeschlagen:', apiError);
    }
    
    setLoading(false);
  };

  const handleClearSelection = () => {
    setSelectedWerk(undefined);
    setError(null);
  };

  if (availableWerks.length === 0) {
    return (
      <Card style={{ marginTop: 24 }}>
        <Alert 
          message="Keine Werke verfügbar" 
          description="In der MRP-Datei wurden keine Werk-Werte gefunden." 
          type="warning" 
          showIcon 
        />
      </Card>
    );
  }

  return (
    <Card style={{ marginTop: 24 }}>
      <Title level={4}>
        <BuildOutlined /> Werk-Auswahl
      </Title>
      <Text>
        Bitte wählen Sie ein Werk aus, um die MRP-Daten zu filtern. Dies stellt sicher, dass 
        Materialien mit mehreren Werk-Einträgen die korrekten Produktions-/Lieferzeiten für 
        Ihre ausgewählte Fertigungsstätte verwenden.
      </Text>
      
      <div style={{ marginTop: 16, display: 'flex', gap: 16, alignItems: 'end', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Text strong>Werk auswählen:</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Wählen Sie ein Werk aus der Liste"
            value={selectedWerk}
            onChange={handleWerkChange}
            disabled={disabled || loading}
            showSearch
            filterOption={(input, option) => {
              if (option && option.value && typeof option.value === 'string') {
                return option.value.toLowerCase().includes(input.toLowerCase());
              }
              return false;
            }}
          >
            {availableWerks.map((werk) => (
              <Option key={werk} value={werk}>
                {werk}
              </Option>
            ))}
          </Select>
        </div>
        
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            onClick={handleConfirmSelection}
            disabled={!selectedWerk || disabled || loading}
            loading={loading}
          >
            {loading ? 'Verarbeitung...' : 'Auswahl bestätigen'}
          </Button>
          
          <Button
            onClick={handleClearSelection}
            disabled={!selectedWerk || disabled || loading}
          >
            Löschen
          </Button>
        </div>
      </div>
      
      {loading && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Spin size="small" /> 
          <Text style={{ marginLeft: 8 }} type="secondary">
            Verarbeite MRP-Daten für Werk "{selectedWerk}"...
          </Text>
        </div>
      )}
      
      {error && (
        <Alert 
          message="Auswahl-Fehler" 
          description={error} 
          type="error" 
          showIcon 
          closable 
          style={{ marginTop: 16 }} 
          onClose={() => setError(null)} 
        />
      )}
      
      <div style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          Verfügbare Werke: {availableWerks.length} • Ausgewählt: {selectedWerk || 'Keines'}
        </Text>
      </div>
    </Card>
  );
};

export default WerkSelection;